import { FaceCullingMode, } from '@skand/three-js-renderer';
import { Color, Matrix4, Quaternion, Sphere, Vector3 } from 'three';
import { PathType } from '../../../CoreModules';
import { calculateCentroid, computeContainerSphere, OBB } from '../../../Utils';
/**
 * Default button colors.
 */
const IDLE_COLORS = [new Color(0xff7798), new Color(0x3ef07e), new Color(0x58bcfd)];
/**
 * Button colors when hovered over.
 */
const HOVER_COLORS = [new Color(0xfb3463), new Color(0x1cd760), new Color(0x18a0fb)];
export class ClippingBoxTool {
    constructor(renderer, widgetManager, navigationService) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "widgetManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigationService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onClip", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "targets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "clippingGroup", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "obb", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "boundingOutlineMesh", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "boundingContainerMesh", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "boundingOutlinePrimitive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "boundingContainerPrimitive", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "translationButtons", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotationButtons", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "translationButtonMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotationButtonMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotationEnabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.renderer = renderer;
        this.widgetManager = widgetManager;
        this.navigationService = navigationService;
        this.onClip = () => { };
        this.targets = new Set();
        this.clippingGroup = renderer.createClippingGroup();
        this.clippingGroup.setEnabled(true);
        for (let i = 0; i < 6; i++) {
            this.clippingGroup.planes.push({
                position: new Vector3(),
                normal: new Vector3(),
            });
        }
        this.obb = new OBB();
        this.boundingOutlineMesh = this.renderer.createLineMesh();
        this.boundingContainerMesh = this.renderer.createIndexedTriangleMesh();
        const { outline, container } = this.createBoundingVolumePrimitives();
        this.boundingOutlinePrimitive = outline;
        this.boundingContainerPrimitive = container;
        this.translationButtons = this.createTranslationButtons();
        this.rotationButtons = this.createRotationButtons();
        this.translationButtonMap = new Map();
        this.rotationButtonMap = new Map();
        this.rotationEnabled = false;
        this.running = false;
        this.visible = false;
    }
    /**
     * Create the bounding volume mesh.
     *
     * @returns
     */
    createBoundingVolumePrimitives() {
        const outline = this.renderer.getObjectFactory().createRawMeshObject({
            mesh: this.boundingOutlineMesh,
            material: {
                type: 'basicOpaque',
                color: { r: 1, g: 1, b: 1 },
            },
            position: {
                ecef: { x: 0, y: 0, z: 0 },
            },
        });
        const container = this.renderer.getObjectFactory().createRawMeshObject({
            mesh: this.boundingContainerMesh,
            material: {
                type: 'basicAlpha',
                color: { r: 1, g: 1, b: 1 },
                alpha: 0.1,
                faceCulling: FaceCullingMode.None,
            },
            position: {
                ecef: { x: 0, y: 0, z: 0 },
            },
        });
        return { outline, container };
    }
    /**
     * Create translation buttons.
     *
     * @returns
     */
    createTranslationButtons() {
        const translationButtons = [];
        let colorIndex = 0;
        for (let i = 0; i < 6; i++) {
            if (i && i % 2 === 0)
                colorIndex++;
            const widget = this.widgetManager.createSphereWidget(5, IDLE_COLORS[colorIndex]);
            translationButtons.push(widget);
        }
        return translationButtons;
    }
    /**
     * Create the rotation buttons.
     *
     * @returns
     */
    createRotationButtons() {
        return IDLE_COLORS.map(color => this.widgetManager.createRingWidget(75, color));
    }
    /**
     * Update the bounding volume primitives.
     */
    updateBoundingPrimitive() {
        const { vertices, indices } = this.obb.getTriangleMeshVertexArray();
        const centroid = calculateCentroid(vertices);
        for (const vertex of vertices) {
            vertex.sub(centroid);
        }
        this.boundingContainerMesh.vertices = vertices;
        this.boundingContainerMesh.indices = indices;
        this.boundingOutlineMesh.vertices = [
            vertices[0],
            vertices[1],
            vertices[1],
            vertices[5],
            vertices[0],
            vertices[2],
            vertices[2],
            vertices[3],
            vertices[1],
            vertices[3],
            vertices[4],
            vertices[5],
            vertices[4],
            vertices[0],
            vertices[4],
            vertices[6],
            vertices[6],
            vertices[7],
            vertices[7],
            vertices[5],
            vertices[7],
            vertices[3],
            vertices[6],
            vertices[2],
        ];
        this.boundingContainerMesh.resolve();
        this.boundingOutlineMesh.resolve();
        this.boundingContainerPrimitive.setPositionECEF(centroid);
        this.boundingOutlinePrimitive.setPositionECEF(centroid);
    }
    /**
     * Update the clipping group of a target model.
     */
    updateClippingGroupFromOBB() {
        const planes = this.obb.getInwardPlanes();
        for (let i = 0; i < 6; i++) {
            const plane = planes[i];
            this.clippingGroup.planes[i].position = plane.position;
            this.clippingGroup.planes[i].normal = plane.normal;
        }
        this.clippingGroup.resolvePlanes();
        this.onClip();
    }
    /**
     * Update the translation buttons paths.
     */
    updateTranslationButtons() {
        const planes = this.obb.getInwardPlanes();
        for (let i = 0; i < 6; i++) {
            const plane = planes[i];
            const button = this.translationButtons[i];
            button.setPath({
                type: PathType.Line,
                origin: plane.position,
                direction: plane.normal,
            });
            button.resetProgress();
            this.translationButtonMap.set(button, plane);
        }
    }
    /**
     * Update the rotation buttons paths.
     */
    updateRotationButtons() {
        const planes = this.obb.getInwardPlanes();
        for (let i = 0; i < 3; i++) {
            const plane = planes[i * 2];
            const orthogonalPlane = planes[(i * 2 + 2) % 6];
            const button = this.rotationButtons[i];
            const center = this.obb.center.clone();
            const origin = this.obb.center
                .clone()
                .add(orthogonalPlane.normal.clone().multiplyScalar(500));
            const normal = plane.normal;
            button.setPath({
                type: PathType.Circle,
                center,
                origin,
                normal,
                pivotCenter: true,
            });
            // Rotate the ring buttons to face normal direction
            const transform = new Matrix4();
            transform.lookAt(center, center.clone().add(normal), new Vector3(0, 1, 0));
            const rotation = new Quaternion();
            rotation.setFromRotationMatrix(transform);
            button.setRotation(rotation);
            this.rotationButtonMap.set(button, plane.normal);
        }
    }
    /**
     * Register the control handlers for the bounding
     * volume scaling translationButtons
     *
     * This will modify the clipping planes directly, then update the OBB
     */
    handleTranslationButtonControls() {
        for (let i = 0; i < 6; i++) {
            const button = this.translationButtons[i];
            button.unregisterAll();
            button.register('hover', () => {
                button.setColor(HOVER_COLORS[Math.floor(i / 2)]);
            });
            button.register('leave', () => {
                if (button.isGrabbed())
                    return;
                button.setColor(IDLE_COLORS[Math.floor(i / 2)]);
            });
            button.register('grab', () => {
                this.navigationService.disable();
                if (this.rotationEnabled) {
                    for (const rotButton of this.rotationButtons) {
                        rotButton.hide();
                    }
                }
            });
            button.register('release', () => {
                this.navigationService.enable();
                if (this.rotationEnabled) {
                    for (const rotButton of this.rotationButtons) {
                        rotButton.show();
                    }
                }
            });
            button.register('move', moveData => {
                const selectedPlane = this.translationButtonMap.get(button);
                if (!selectedPlane)
                    return;
                const selectedNormal = new Vector3(selectedPlane.normal.x, selectedPlane.normal.y, selectedPlane.normal.z);
                const orthogonalDisplacement = moveData.position
                    .clone()
                    .sub(moveData.prevPosition)
                    .divideScalar(2);
                // Move the other translationButtons on planes orthogonal to this one as well,
                // relative to the selected plane, to keep the bounding box convex
                for (const otherButton of this.translationButtons) {
                    const plane = this.translationButtonMap.get(otherButton);
                    if (otherButton === button || !plane)
                        continue;
                    const normal = new Vector3(plane.normal.x, plane.normal.y, plane.normal.z);
                    const cross = normal.clone().cross(selectedNormal);
                    if (cross.length() >= 0.001) {
                        otherButton.setPath({
                            type: PathType.Line,
                            origin: otherButton.getPosition().add(orthogonalDisplacement),
                            direction: normal,
                        });
                        otherButton.resetProgress();
                    }
                }
                // Update the bounding box volume
                const faceCenters = this.translationButtons.map(button => button.getPosition());
                this.obb.setFromFaceCenters(faceCenters);
                this.updateClippingGroupFromOBB();
                this.updateBoundingPrimitive();
                this.updateRotationButtons();
            });
        }
    }
    /**
     * Register control handlers for the bounding volume
     * rotation buttons
     *
     * This will modify the OBB to update the clipping group
     */
    handleRotationButtonControls() {
        for (let i = 0; i < 3; i++) {
            const button = this.rotationButtons[i];
            button.unregisterAll();
            button.register('hover', () => {
                button.setColor(HOVER_COLORS[i]);
            });
            button.register('leave', () => {
                if (button.isGrabbed())
                    return;
                button.setColor(IDLE_COLORS[i]);
            });
            button.register('grab', () => {
                this.navigationService.disable();
                for (const otherButton of this.rotationButtons) {
                    if (button === otherButton)
                        continue;
                    otherButton.hide();
                }
            });
            button.register('release', () => {
                this.navigationService.enable();
                for (const otherButton of this.rotationButtons) {
                    otherButton.show();
                }
            });
            button.register('move', moveData => {
                const normal = this.rotationButtonMap.get(button);
                if (!normal)
                    return;
                const { deltaProgress } = moveData;
                const angle = deltaProgress * Math.PI * 2;
                const quaternion = new Quaternion();
                quaternion.setFromAxisAngle(normal, angle);
                this.obb.rotate(quaternion);
                this.updateClippingGroupFromOBB();
                this.updateBoundingPrimitive();
                this.updateTranslationButtons();
            });
        }
    }
    start(onClip) {
        this.running = true;
        for (const target of this.targets) {
            this.clippingGroup.addObject(target.getWorldObject());
        }
        this.onClip = onClip;
    }
    stop() {
        this.running = false;
        this.clippingGroup.clear();
    }
    show() {
        this.visible = true;
        this.handleTranslationButtonControls();
        for (const button of this.translationButtons) {
            button.show();
        }
        if (this.rotationEnabled) {
            this.handleRotationButtonControls();
            for (const button of this.rotationButtons) {
                button.show();
            }
        }
        this.renderer.getWorld().addObject(this.boundingContainerPrimitive);
        this.renderer.getWorld().addObject(this.boundingOutlinePrimitive);
    }
    hide() {
        this.visible = false;
        for (const button of this.translationButtons) {
            button.unregisterAll();
            button.hide();
        }
        for (const button of this.rotationButtons) {
            button.unregisterAll();
            button.hide();
        }
        this.renderer.getWorld().removeObject(this.boundingContainerPrimitive);
        this.renderer.getWorld().removeObject(this.boundingOutlinePrimitive);
    }
    isActive() {
        return this.running;
    }
    isVisible() {
        return this.visible;
    }
    addTarget(target) {
        this.targets.add(target);
        if (this.running) {
            this.clippingGroup.addObject(target.getWorldObject());
        }
    }
    removeTarget(target) {
        this.targets.delete(target);
        this.clippingGroup.removeObject(target.getWorldObject());
    }
    getTargets() {
        return Array.from(this.targets);
    }
    clear() {
        this.targets.clear();
        this.clippingGroup.clear();
    }
    /**
     * Reset the clipping box.
     */
    reset() {
        const spheres = [];
        for (const target of this.targets) {
            const sphere = new Sphere(target.getPosition(), target.getBoundingRadius());
            spheres.push(sphere);
        }
        this.obb.setFromGeoSphere(computeContainerSphere(spheres));
        this.updateClippingGroupFromOBB();
        this.updateBoundingPrimitive();
        this.updateTranslationButtons();
        this.updateRotationButtons();
    }
    /**
     * Enable clipping box rotation controls.
     */
    enableRotate() {
        this.rotationEnabled = true;
        if (this.visible) {
            this.handleRotationButtonControls();
            for (const button of this.rotationButtons) {
                button.show();
            }
        }
    }
    /**
     * Disable clipping box rotation controls.
     */
    disableRotate() {
        this.rotationEnabled = false;
        for (const button of this.rotationButtons) {
            button.unregisterAll();
            button.hide();
        }
    }
    /**
     * Get the OBB of the clipping box.
     *
     * @returns
     */
    getOBB() {
        return this.obb.clone();
    }
    /**
     * Set the OBB of the clipping box.
     *
     * @param obb
     * @returns
     */
    setOBB(obb) {
        this.obb = obb.clone();
        this.updateClippingGroupFromOBB();
        this.updateBoundingPrimitive();
        this.updateTranslationButtons();
        this.updateRotationButtons();
    }
}
